import {ReactNode, useState} from 'react';
import {styled} from '@mui/material/styles';
import {
  Card,
  CardContent,
  CardActions,
  Collapse,
  IconButton,
  IconButtonProps,
  CardProps,
  SxProps,
  Theme,
  Box,
} from '@mui/material';
import {ExpandMore} from '@mui/icons-material';
import {getAreWeRunningInMobile} from '../../../utils/functions';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
  isLoading: boolean;
}

const ExpandMoreStyled = styled((props: ExpandMoreProps) => {
  const {expand, isLoading, ...other} = props;
  return <IconButton {...other} />;
})(({theme, isLoading, expand}) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  ...(isLoading ? {opacity: 0.5, cursor: 'not-allowed'} : {opacity: 1, cursor: 'pointer'}),
}));

interface CardCollapseProps {
  collapsableHeader: ReactNode;
  children: ReactNode;
  isLoading?: boolean;
  defaultExpand?: boolean;
  cardProps?: CardProps;
  onChange?: (expanded: boolean) => void;
  disabled?: boolean;
}

export const CardCollapse = ({
  collapsableHeader,
  children,
  cardProps,
  isLoading = false,
  defaultExpand = false,
  onChange,
  disabled,
}: CardCollapseProps) => {
  const [expanded, setExpanded] = useState(defaultExpand);

  const handleExpandClick = () => {
    onChange && onChange(!expanded);
    setExpanded((state) => !state);
  };

  const cardSx: SxProps<Theme> = {
    gap: '10px',
    padding: '5px 10px',
    cursor: isLoading ? 'not-allowed' : getAreWeRunningInMobile() ? 'none' : 'pointer',
  };

  const cardContentSx: SxProps<Theme> = {
    padding: '6px',
    '&.MuiCardContent-root:last-child': {
      padding: '6px 6px 0 6px',
    },
  };

  return (
    <Card {...cardProps}>
      <CardActions disableSpacing sx={cardSx} onClick={disabled ? undefined : handleExpandClick}>
        <Box sx={{width: '100%'}}>{collapsableHeader}</Box>
        {!disabled && (
          <ExpandMoreStyled
            expand={expanded && !isLoading}
            isLoading={isLoading}
            aria-expanded={expanded && !isLoading}
            aria-label="show more"
            size="small"
          >
            <ExpandMore
              sx={{
                borderRadius: '100%!important',
                htmlColor: 'var(--dcm-gray)',
              }}
            />
          </ExpandMoreStyled>
        )}
      </CardActions>
      <Collapse in={expanded && !isLoading} timeout="auto" unmountOnExit>
        <CardContent sx={cardContentSx}>{children}</CardContent>
      </Collapse>
    </Card>
  );
};
